import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useBetween } from "use-between";

import {
    Checkout,
    Collection,
    // Dashboard,
    Login,
    Product,
    Details,
    Favorites,
    Orders,
    Order,
    Notifications,
    Password,
    Search,
    Forgot,
    PaymentPickup,
} from "./pages";
import Data from "./pages/data";
import Delivery from "./pages/delivery";
import ContentManagement from "./pages/content";
import Privacy from "./pages/privacy";
import useApi from "./pages/useApi";
import Request from "./pages/request";
import AccountSwitcher from "./pages/accountSwitch";
import Landing from "./pages/landing";
import ResetPassword from "./pages/resetPassword";
import OpenInvoices from "./pages/openInvoices";
import NewContents from "./pages/content/createOrUpdateContents";
import OpenInvoice from "./pages/openInvoice";
import { useCallback, useEffect, useState } from "react";
import RepresentativeDetails from "./pages/Representative-details";

const RequireAuth = ({ children }) => {
    const { userId, customerId, getCustomer, logout } = useBetween(useApi);
    const location = useLocation();
    const [isValidating, setIsValidating] = useState(true);
    const [isSessionValid, setIsSessionValid] = useState(true);

    const validateSession = useCallback(() => {
        if (!customerId || !userId) {
            setIsValidating(false);
            setIsSessionValid(false);
            return;
        }

        setIsValidating(true);
        getCustomer(customerId)
            .then(() => {
                setIsSessionValid(true);
            })
            .catch(() => {
                // If getCustomer fails (expired token, network error, etc.)
                // the axios interceptor handles 401 redirect automatically.
                // For non-401 errors, we also force logout to avoid blank pages.
                setIsSessionValid(false);
                logout();
            })
            .finally(() => {
                setIsValidating(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, userId]);

    useEffect(() => {
        validateSession();
    }, [validateSession]);

    if (!userId || !isSessionValid) {
        return (
            <Navigate to='/login' replace state={{ path: location.pathname }} />
        );
    }

    if (isValidating) {
        return null;
    }

    return children;
};

const App = () => {
    return (
        <>
            <Routes>
                <Route
                    path='/'
                    element={
                        <RequireAuth>
                            <Landing />
                        </RequireAuth>
                    }
                />
                <Route path='/login' element={<Login />} />
                <Route path='/forgot-password' element={<Forgot />} />
                <Route path='/reset-password' element={<ResetPassword />} />
                <Route path='/request-access' element={<Request />} />
                <Route path='/accounts' element={<AccountSwitcher />} />
                <Route
                    path='/collection/:brand'
                    element={
                        <RequireAuth>
                            <Collection />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/search'
                    element={
                        <RequireAuth>
                            <Search />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/product/:id'
                    element={
                        <RequireAuth>
                            <Product />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/checkout'
                    element={
                        <RequireAuth>
                            <Checkout />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/details'
                    element={
                        <RequireAuth>
                            <Details />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/favorites'
                    element={
                        <RequireAuth>
                            <Favorites />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/orders'
                    element={
                        <RequireAuth>
                            <Orders />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/orders/:id'
                    element={
                        <RequireAuth>
                            <Order />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/open-invoices/:id/schedule-payment-pickup'
                    element={
                        <RequireAuth>
                            <PaymentPickup />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/notifications'
                    element={
                        <RequireAuth>
                            <Notifications />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/password'
                    element={
                        <RequireAuth>
                            <Password />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/contents'
                    element={
                        <RequireAuth>
                            <ContentManagement />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/contents/:id/update'
                    element={
                        <RequireAuth>
                            <NewContents edit={true} />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/contents/create'
                    element={
                        <RequireAuth>
                            <NewContents />
                        </RequireAuth>
                    }
                />                <Route
                    path='/profile/privacy'
                    element={
                        <RequireAuth>
                            <Privacy />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/data'
                    element={
                        <RequireAuth>
                            <Data />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/delivery'
                    element={
                        <RequireAuth>
                            <Delivery />
                        </RequireAuth>
                    }
                />
                <Route
                    path='/profile/open-invoices'
                    element={
                        <RequireAuth>
                            <OpenInvoices />
                        </RequireAuth>
                    }
                />

                <Route
                    path='/profile/representative'
                    element={
                        <RequireAuth>
                            <RepresentativeDetails />
                        </RequireAuth>
                    }
                />

                <Route
                    path='/profile/open-invoices/:id'
                    element={
                        <RequireAuth>
                            <OpenInvoice />
                        </RequireAuth>
                    }
                />
            </Routes>
            <ToastContainer
                position='top-right'
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                draggable
            />
        </>
    );
};

export default App;
